import { Rule } from 'antd/lib/form';
import { CSSProperties, ReactNode } from 'react';

export interface PageResponse {
    data: any;
    pageNumber: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

export interface PatientSearchResponse extends PageResponse {
    data: any;
}

export interface PatientResultDataResponse {
    hrxPatientId: string;
    patientId: string;
    medicalRecordId: string;
    hospiceId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    teamCode: string;
    clinicianHasAccess: Boolean;
    patientStatus: Number;
    patientStatusDisplay: string;
}

export interface PatientSearchRequest {
    hrxPatientId: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
}

export interface PatientDetailsResponse {
    patientId: string;
    hrxPatientId: string;
    medicalRecordId: string;
    scriptSurePatientId: number;
    hospiceOrganizationId: string;
    hospiceOrganizationImplementationDate: string;
    teamCode: string;
    locationOfServiceId: string;
    consent: boolean;
    patientStatus: PatientStatusEnum;
    dischargedOnEmr: boolean;
    patientStatusDisplay: string;
    admissionAddress: {
        streetAddress: string;
        city: string;
        state: string;
        zipCode: string;
        county: string;
        country: string;
    };
    demographics: {
        firstName: string;
        middleName: string | null;
        lastName: string;
        dateOfBirth: string;
        socialSecurityNumber: string | null;
        gender: string;
        race: string | null;
        religion: string | null;
        language: string;
        citizenship: string;
        maritalStatus: string;
        phoneNumber: {
            home: string;
            office: string | null;
            mobile: string | null;
        };
        address: {
            streetAddress: string;
            city: string;
            state: string;
            zipCode: string;
            county: string;
            country: string;
        };
        notes: string | null;
    };
    diagnoses: any[];
    primaryCarePractitionerUserId: string;
    identifiers: {
        identifier: string;
        identifierType: string;
    }[];
    allergies: any[];
    locationOfServiceOptions: {
        id: string;
        subGroupId: string | null;
        name: string;
    }[];
    primaryCarePractitioner: {
        userId: string;
        firstName: string;
        lastName: string;
        npi: string;
    };
    eligibilityStartDate: string;
    eligibilityEndDate: string;
}

export interface PatientScriptSureCookieResponse {
    apiUrl: string;
    apiVersion: string;
    webAppUrl: string;
    sessiontoken: string;
    hasPrescriberAccess: boolean;
    cookie: {
        'connect.sid': string;
        'Max-Age': string;
        Domain: string;
        Path: string;
        Expires: string;
        Secure: string;
        SameSite: string;
    };
}
export interface PatientScriptSureCookieRequest {
    SessionToken: string;
    PatientId: string;
}

export interface PatientDischargeRequest {
    dischargeReason: number;
    date: string;
    note: string;
}

export interface PatientDischargeResponse {
    id: string;
}

export interface PatientConsultationRequest {
    consultReason: number;
    isUrgentRequest: boolean;
    notes: string;
    mobilePhoneNumber: string;
    emailAddress: string;
    directLine: string;
    isContactInfoConfirmed: boolean;
    minuteDuration: number;
    patientIds: string[];
}

export interface DropdownOptionsResponse {
    consultReasons: { [key: string]: string };
    dischargeReasons: { [key: string]: string };
    languageOptions: { [key: string]: string };
    raceOptions: string[];
    religionOptions: string[];
}

export interface PatientConsultationResponse {
    id: string;
}

export enum PatientStatusEnum {
    Invalid,
    ReferralNewPatient,
    Admitted,
    DischargedDeceased,
    DischargedRevoked,
    DischargedTransferred,
}

export interface PatientField {
    label: string;
    key: string;
    dataField: string[];
    placeHolder: string;
    mandatory: boolean;
    validator?: (rule: Rule, value: any) => Promise<any>;
    fieldType: string;
    note?: string | ReactNode;
    options?: Array<{ label: string; value: string; original?: any }>;
    onChange?: (
        value: string | React.ChangeEvent<HTMLInputElement>,
    ) => Promise<void>;
    style?: CSSProperties;
}
export interface SearchDrugListResponse {
    routeD_MED_ID: number;
    meD_NAME_ID: number;
    meD_ROUTE_ID: number;
    formularyStatus: number;
    hasNdc: number;
    meD_ROUTED_MED_ID_DESC: string;
    gcN_SEQNO: string;
    meD_NAME_TYPE_CD: string;
    meD_STATUS_CD: string;
    drugGroup: string;
    pharmacology: string;
    genericName: string;
    typeId: string;
    multipletype: false;
    id: string;
    domainEvents: [];
}
export interface SearchDrugFormatListResponse {
    routeD_MED_ID: number;
    gcN_SEQNO: number;
    meD_MEDID_DESC: string;
    meD_REF_DEA_CD: string;
    meD_ROUTED_MED_ID_DESC: string;
    meD_STATUS_CD: string;
    meD_STRENGTH: string;
    meD_STRENGTH_UOM: string;
    meD_DOSAGE_FORM_DESC: string;
    epn: string;
    rxNorm: string;
    rxNormQualifier: string;
    meD_REF_GEN_DRUG_NAME_CD: string;
    meD_REF_FED_LEGEND_IND: string;
    meD_NAME_TYPE_CD: string;
    imk_preferred_ind: string;
    ndc: string;
    id: string;
    domainEvents: [];
}

export interface MedOrderRequest {
    orderSetId?: string;
    ROUTED_MED_ID: number;
    GCN_SEQNO: number;
    rxNorm: string;
    rxNormQualifier: string;
    quantity: number;
    orderSource: number;
    ndc: string;
    drugName: string;
    quantityQualifier: string;
    drugFormat: string;
    directions: string;
    isDescriptionStructured: boolean;
    structuredDirections: {
        Quantity: number;
        ActionCode: string;
        FormCode: string;
        RouteCode: string;
        TimingCode: string;
        TimingCodeText: string;
        FrequencyUnitCode: string;
    };
}

export interface QueueMedOrderRequest {
    data: MedOrderRequest;
    patientId: string;
}

export interface QueueMedOrderResponse {
    routedMedId: number;
    gcnSeqNo: number;
    quantity: number;
    status: number;
    source: number;
    scriptSureOrderId: null;
    scriptSurePatientId: null;
    id: string;
    drugName: string;
    ndc: string;
    drugFormat: string;
    directions: string;
    patientId: string;
    createdOn: string;
    lastModifiedOn: string;
}

export interface UpdateMedOrderRequest {
    data: MedOrderRequest;
    patientId: string;
    orderId: string;
}

export enum OrderStatusEnum {
    Active,
    Inactive,
    Pending,
    Draft,
}

export enum OrderSourceEnum {
    NaviScript,
    EMR,
    ScriptSure,
}

export enum OrderStatusEmrEnum {
    NotStarted = 0,
    Success = 1,
    Error = 2,
    Processing = 3,
    UiError = 4,
    MedOrderNotFoundError = 5,
    TeamNotFoundError = 6,
    MedOrderMissesDataError = 7,
    PatientNotFoundError = 8,
    SaveError = 9,
    PendingImport = 10,
    DuplicateError = 11,
    PotentialDuplicateError = 12,
    NA = 13,
}

export interface OrderResponse {
    id: string;
    drugName: string;
    routedMedId: number;
    gcnSeqNo: number;
    ndc: string;
    drugFormat: string;
    quantity: number;
    directions: string;
    scriptSureOrderId: number | null;
    scriptSurePatientId: number | null;
    status: OrderStatusEnum;
    source: OrderSourceEnum;
    orderStatusEmr: OrderStatusEmrEnum;
    orderEmrId: string | null;
    patientId: string;
    createdOn: string;
    lastModifiedOn: string | null;
    isDescriptionStructured: boolean;
    structuredDirections: {
        quantity: number;
        actionCode: string;
        formCode: string;
        routeCode: string;
        timingCode: string;
        frequencyUnitCode: string;
    };
}
export interface MedOrderResponse {
    mrn: null;
    teamCode: null;
    notes: [];
    medicationStatus: number;
    orderStatusEmr: number;
    orderSource: number;
    medicationId: string;
    patientId: string;
    payerParty: string;
    emrPatientId: string;
    nameDisplay: string;
    unitDosageDisplay: string;
    strength: string;
    strengthUnit: string;
    dosage: string;
    dosageUnit: string;
    durationDisplay: string;
    duration: string;
    durationUnit: string;
    routeDisplay: string;
    frequencyDisplay: string;
    indicationsDisplay: string;
    quantity: string;
    quantityUnit: string;
    startDate: string;
    endDate: string;
    writtenDate: string;
    medicationStatusDisplay: string;
}

export interface OrderUpdates {
    successfulIds: string[];
    failedIds: string[];
}

export interface OrderTemplate {
    orderTemplate: boolean | undefined;
    id: string;
    drugName: string;
    drugFormat?: string;
    routedMedId: number;
    gcnSeqNo: number;
    quantity: number;
    directions: string;
    orderEmrId?: string;
    source: OrderSourceEnum;
    isSelected: boolean;
}

export interface CreateOrderFromMedSheet {
    medSheetId: string;
    patientId: string;
}

export interface CreateOrdersFromOrderTemplates {
    orderTemplateIds: string[];
    patientId: string;
}

export interface StructuredActionsResponse {
    conceptId: string;
    name: string;
}
export interface StructuredFormResponse {
    potencyUnit: string;
    name: string;
}
export interface StructuredRouteResponse {
    title: string;
    conceptId: string;
    name: string;
}
export interface StructuredFrequenciesResponse {
    multiplier: number;
    frequencyNumericValue: number;
    frequencyUnitsCode: string;
    frequencyUnitsText: string;
    conceptId: string;
    name: string;
}

export interface DrugInteractionResponse {
    interactions: DrugInteraction[];
}

export interface DrugInteraction {
    html: string;
    name: string;
    total: number;
}

export interface DrugInteractionRequest {
    prescribedGcnSeqno: string;
    prescribedRoutedMedId: number;
    patientId: number;
    drugName: string;
}
